// src/components/settings/PaymentsTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  CreditCard, ExternalLink, CheckCircle, AlertCircle, 
  Clock, XCircle, Loader2, Unlink, RefreshCw, Shield,
  DollarSign, Building2, ArrowRight
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import ConfirmModal from '../ConfirmModal';

const PaymentsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState({
    acceptsVenmo: false,
    venmoHandle: '',
    acceptsZelle: false,
    zelleInfo: '',
    acceptsCashApp: false,
    cashAppHandle: '',
    acceptsPayPal: false,
    payPalEmail: '',
    acceptsCash: true,
    acceptsCheck: true,
    checkPayableTo: '',
    acceptsWireTransfer: false,
    wireTransferInfo: ''
  });
  const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
  const [paymentMethodsSaved, setPaymentMethodsSaved] = useState(false);

  useEffect(() => {
    loadStatus();
    loadPaymentMethods();
    
    // Check for return from Stripe
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'complete') {
      setSuccessMessage('Stripe account connected! Verifying status...');
      // Remove the query param
      searchParams.delete('stripe');
      setSearchParams(searchParams, { replace: true });
      // Reload status after a brief delay
      setTimeout(() => loadStatus(), 1500);
    } else if (stripeParam === 'refresh') {
      // User needs to restart onboarding
      setError('Onboarding session expired. Please try again.');
      searchParams.delete('stripe');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/stripe-connect/status');
      setStatus(response.data);
      
      if (response.data.isConnected && response.data.chargesEnabled) {
        setSuccessMessage(null); // Clear any pending success message
      }
    } catch (err) {
      console.error('Error loading Stripe status:', err);
      setError('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await api.get('/business/payment-methods');
      setPaymentMethods({
        acceptsVenmo: response.data.acceptsVenmo,
        venmoHandle: response.data.venmoHandle || '',
        acceptsZelle: response.data.acceptsZelle,
        zelleInfo: response.data.zelleInfo || '',
        acceptsCashApp: response.data.acceptsCashApp,
        cashAppHandle: response.data.cashAppHandle || '',
        acceptsPayPal: response.data.acceptsPayPal,
        payPalEmail: response.data.payPalEmail || '',
        acceptsCash: response.data.acceptsCash,
        acceptsCheck: response.data.acceptsCheck,
        checkPayableTo: response.data.checkPayableTo || '',
        acceptsWireTransfer: response.data.acceptsWireTransfer,
        wireTransferInfo: response.data.wireTransferInfo || ''
      });
    } catch (err) {
      console.error('Error loading payment methods:', err);
    }
  };

  const savePaymentMethods = async () => {
    try {
      setSavingPaymentMethods(true);
      setPaymentMethodsSaved(false);
      setError(null);
      
      await api.put('/business/payment-methods', paymentMethods);
      
      setPaymentMethodsSaved(true);
      setSuccessMessage('Payment methods updated successfully!');
      
      // Reset saved state after 3 seconds
      setTimeout(() => setPaymentMethodsSaved(false), 3000);
      // Keep success message longer
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error saving payment methods:', err);
      setError(err.response?.data?.error || 'Failed to save payment methods');
    } finally {
      setSavingPaymentMethods(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      const response = await api.post('/stripe-connect/onboarding');
      
      // Redirect to Stripe's hosted onboarding
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error starting onboarding:', err);
      setError(err.response?.data?.error || 'Failed to start Stripe onboarding');
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await api.post('/stripe-connect/dashboard-link');
      window.open(response.data.url, '_blank');
    } catch (err) {
      console.error('Error opening dashboard:', err);
      setError('Failed to open Stripe dashboard');
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await api.post('/stripe-connect/disconnect');
      setStatus({
        isConnected: false,
        accountId: null,
        status: null,
        chargesEnabled: false,
        payoutsEnabled: false,
        detailsSubmitted: false,
        requirements: [],
        dashboardUrl: null
      });
      setShowDisconnectModal(false);
      setSuccessMessage('Stripe account disconnected');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError(err.response?.data?.error || 'Failed to disconnect Stripe account');
    } finally {
      setDisconnecting(false);
    }
  };

  const getStatusBadge = () => {
    if (!status?.isConnected) return null;

    const statusConfig = {
      enabled: { 
        icon: CheckCircle, 
        color: 'text-green-600 bg-green-50 border-green-200', 
        label: 'Active' 
      },
      pending: { 
        icon: Clock, 
        color: 'text-yellow-600 bg-yellow-50 border-yellow-200', 
        label: 'Pending Setup' 
      },
      pending_verification: { 
        icon: Clock, 
        color: 'text-blue-600 bg-blue-50 border-blue-200', 
        label: 'Pending Verification' 
      },
      restricted: { 
        icon: AlertCircle, 
        color: 'text-orange-600 bg-orange-50 border-orange-200', 
        label: 'Action Required' 
      },
      disabled: { 
        icon: XCircle, 
        color: 'text-red-600 bg-red-50 border-red-200', 
        label: 'Disabled' 
      },
    };

    const config = statusConfig[status.status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-medium ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-10 bg-gray-200 rounded w-40 mt-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="card bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white rounded-xl shadow-sm">
            <CreditCard className="h-8 w-8 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Accept Customer Payments</h2>
            <p className="text-sm text-gray-600">
              Connect your Stripe account to accept credit card payments directly from your customers 
              through invoice payment links.
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
          <span className="text-red-800">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <XCircle size={18} />
          </button>
        </div>
      )}

      {/* Connection Status Card */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Stripe Connection</h3>
          <button
            onClick={loadStatus}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh status"
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {!status?.isConnected ? (
          // Not Connected State
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Payment Account Connected</h4>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Connect your Stripe account to start accepting credit card payments from customers. 
              Setup takes about 5 minutes.
            </p>
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              {connecting ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Connecting...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Connect with Stripe
                </>
              )}
            </button>
          </div>
        ) : (
          // Connected State
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Stripe Account</p>
                  <p className="text-sm text-gray-500">{status.accountId}</p>
                </div>
              </div>
              {getStatusBadge()}
            </div>

            {/* Status Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Card Payments</p>
                <div className="flex items-center gap-2">
                  {status.chargesEnabled ? (
                    <>
                      <CheckCircle size={16} className="text-green-600" />
                      <span className="font-medium text-green-700">Enabled</span>
                    </>
                  ) : (
                    <>
                      <Clock size={16} className="text-yellow-600" />
                      <span className="font-medium text-yellow-700">Pending</span>
                    </>
                  )}
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payouts</p>
                <div className="flex items-center gap-2">
                  {status.payoutsEnabled ? (
                    <>
                      <CheckCircle size={16} className="text-green-600" />
                      <span className="font-medium text-green-700">Enabled</span>
                    </>
                  ) : (
                    <>
                      <Clock size={16} className="text-yellow-600" />
                      <span className="font-medium text-yellow-700">Pending</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Requirements Warning */}
            {status.requirements?.length > 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="font-medium text-yellow-800">Action Required</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Stripe needs additional information to fully activate your account.
                    </p>
                    <button
                      onClick={handleConnect}
                      className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                    >
                      Complete Setup →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              {status.chargesEnabled && status.payoutsEnabled && (
                <button
                  onClick={handleOpenDashboard}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <ExternalLink size={16} />
                  Open Stripe Dashboard
                </button>
              )}
              
              {(!status.chargesEnabled || !status.payoutsEnabled) && (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Loading...
                    </>
                  ) : (
                    <>
                      Continue Setup
                    </>
                  )}
                </button>
              )}

              <button
                onClick={() => setShowDisconnectModal(true)}
                className="btn btn-secondary text-red-600 hover:bg-red-50 inline-flex items-center gap-2"
              >
                <Unlink size={16} />
                Disconnect
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Payment Methods Card */}
      <div className="card">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Invoice Payment Methods
          </h3>
          <p className="text-sm text-gray-600">
            Select which payment methods to display on your invoices. Customers will see these options in invoice emails.
          </p>
        </div>

        {/* Payment Methods Grid */}
        <div className="space-y-4">
          {/* Stripe (if connected) */}
          {status?.isConnected && status?.chargesEnabled && (
            <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-purple-900">Online Card Payments (Stripe)</p>
                  <p className="text-sm text-purple-700">Automatically shown on all invoices</p>
                </div>
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          )}

          {/* Venmo */}
          <PaymentMethodOption
            icon={<span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#3D95CE', borderRadius: '6px', color: 'white', fontWeight: 'bold', fontSize: '16px'}}>V</span>}
            name="Venmo"
            enabled={paymentMethods.acceptsVenmo}
            onToggle={(checked) => setPaymentMethods({ ...paymentMethods, acceptsVenmo: checked })}
            inputLabel="Venmo Handle or Phone"
            inputPlaceholder="@your-handle or (555) 123-4567"
            inputValue={paymentMethods.venmoHandle || ''}
            onInputChange={(value) => setPaymentMethods({ ...paymentMethods, venmoHandle: value })}
            helpText="Your Venmo username or phone number"
          />

          {/* Zelle */}
          <PaymentMethodOption
            icon={<span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#6D1ED4', borderRadius: '6px', color: 'white', fontWeight: 'bold', fontSize: '16px'}}>Z</span>}
            name="Zelle"
            enabled={paymentMethods.acceptsZelle}
            onToggle={(checked) => setPaymentMethods({ ...paymentMethods, acceptsZelle: checked })}
            inputLabel="Zelle Email or Phone"
            inputPlaceholder="email@example.com or (555) 123-4567"
            inputValue={paymentMethods.zelleInfo || ''}
            onInputChange={(value) => setPaymentMethods({ ...paymentMethods, zelleInfo: value })}
            helpText="Email or phone registered with Zelle"
          />

          {/* Cash App */}
          <PaymentMethodOption
            icon={<span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#00D64F', borderRadius: '6px', color: 'white', fontWeight: 'bold', fontSize: '16px'}}>$</span>}
            name="Cash App"
            enabled={paymentMethods.acceptsCashApp}
            onToggle={(checked) => setPaymentMethods({ ...paymentMethods, acceptsCashApp: checked })}
            inputLabel="Cash App Handle"
            inputPlaceholder="$yourcashtag"
            inputValue={paymentMethods.cashAppHandle || ''}
            onInputChange={(value) => setPaymentMethods({ ...paymentMethods, cashAppHandle: value })}
            helpText="Your $cashtag (starts with $)"
          />

          {/* PayPal */}
          <PaymentMethodOption
            icon={<span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#0070BA', borderRadius: '6px', color: 'white', fontWeight: 'bold', fontSize: '16px'}}>P</span>}
            name="PayPal"
            enabled={paymentMethods.acceptsPayPal}
            onToggle={(checked) => setPaymentMethods({ ...paymentMethods, acceptsPayPal: checked })}
            inputLabel="PayPal Email"
            inputPlaceholder="paypal@yourbusiness.com"
            inputValue={paymentMethods.payPalEmail || ''}
            onInputChange={(value) => setPaymentMethods({ ...paymentMethods, payPalEmail: value })}
            helpText="Email associated with your PayPal account"
          />

          {/* Cash */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: '#10B981', borderRadius: '8px', color: 'white', fontWeight: 'bold', fontSize: '18px'}}>
                $
              </div>
              <div>
                <p className="font-medium text-gray-900">Cash</p>
                <p className="text-sm text-gray-500">Accept cash payments</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={paymentMethods.acceptsCash}
                onChange={(e) => setPaymentMethods({ ...paymentMethods, acceptsCash: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-100 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm peer-checked:bg-green-500"></div>
            </label>
          </div>

          {/* Check */}
          <PaymentMethodOption
            icon={<span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#6366F1', borderRadius: '6px', color: 'white', fontWeight: 'bold', fontSize: '16px'}}>✓</span>}
            name="Check"
            enabled={paymentMethods.acceptsCheck}
            onToggle={(checked) => setPaymentMethods({ ...paymentMethods, acceptsCheck: checked })}
            inputLabel="Make Checks Payable To"
            inputPlaceholder="Your Business Name, LLC"
            inputValue={paymentMethods.checkPayableTo || ''}
            onInputChange={(value) => setPaymentMethods({ ...paymentMethods, checkPayableTo: value })}
            helpText="Business name for check payments"
          />

          {/* Wire Transfer */}
          <PaymentMethodOption
            icon={<span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#8B5CF6', borderRadius: '6px', color: 'white', fontWeight: 'bold', fontSize: '16px'}}>⇄</span>}
            name="Wire Transfer"
            enabled={paymentMethods.acceptsWireTransfer}
            onToggle={(checked) => setPaymentMethods({ ...paymentMethods, acceptsWireTransfer: checked })}
            inputLabel="Wire Transfer Instructions"
            inputPlaceholder="Bank name, routing #, account #"
            inputValue={paymentMethods.wireTransferInfo || ''}
            onInputChange={(value) => setPaymentMethods({ ...paymentMethods, wireTransferInfo: value })}
            helpText="Banking details for wire transfers"
            isTextArea
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={savePaymentMethods}
            disabled={savingPaymentMethods || paymentMethodsSaved}
            className={`btn inline-flex items-center gap-2 transition-all ${
              paymentMethodsSaved 
                ? 'bg-green-600 hover:bg-green-600 text-white cursor-default' 
                : 'btn-primary'
            }`}
          >
            {savingPaymentMethods ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Saving...
              </>
            ) : paymentMethodsSaved ? (
              <>
                <CheckCircle size={18} />
                Saved!
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Save Payment Methods
              </>
            )}
          </button>
        </div>
      </div>

      {/* How Payments Work - Show when connected */}
      {status?.isConnected && status?.chargesEnabled && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">When Will I Get Paid?</h3>
          
          {/* Simple Timeline */}
          <div className="flex items-center justify-between mb-6 px-2">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Customer Pays</p>
              <p className="text-xs text-gray-500">Instant</p>
            </div>
            
            <ArrowRight className="text-gray-300 flex-shrink-0" size={24} />
            
            {/* Step 2 */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Stripe Holds</p>
              <p className="text-xs text-gray-500">2 business days</p>
            </div>
            
            <ArrowRight className="text-gray-300 flex-shrink-0" size={24} />
            
            {/* Step 3 */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">Bank Transfer</p>
              <p className="text-xs text-gray-500">1-2 business days</p>
            </div>
            
            <ArrowRight className="text-gray-300 flex-shrink-0" size={24} />
            
            {/* Step 4 */}
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-900">In Your Bank</p>
              <p className="text-xs text-gray-500">3-5 days total</p>
            </div>
          </div>
          
          {/* Key Points */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-700">Fully automatic</span> — No action needed. Stripe sends funds to your bank automatically.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-700">Industry standard</span> — Same timing as Square, PayPal, and other processors.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-700">Track payouts</span> — View payout status anytime in your Stripe Dashboard.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-blue-900">Secure & Simple</h4>
            <ul className="mt-2 text-sm text-blue-800 space-y-1">
              <li>• Stripe handles all payment security and PCI compliance</li>
              <li>• Funds go directly to your bank account automatically</li>
              <li>• Processing fee: 2.9% + $0.30 per transaction (Stripe) + 0.5% (SuddyFlow)</li>
              <li>• SuddyFlow never sees your banking information</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Disconnect Confirmation Modal */}
      <ConfirmModal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Stripe Account"
        message="Are you sure you want to disconnect your Stripe account? You won't be able to accept customer payments until you reconnect."
        confirmText={disconnecting ? "Disconnecting..." : "Disconnect"}
        confirmStyle="danger"
      />
    </div>
  );
};

// Helper component for payment method options
const PaymentMethodOption = ({ 
  icon, 
  name, 
  enabled, 
  onToggle, 
  inputLabel, 
  inputPlaceholder, 
  inputValue, 
  onInputChange, 
  helpText,
  isTextArea = false
}) => {
  return (
    <div className={`p-4 rounded-lg border-2 transition-all ${
      enabled ? 'bg-primary-50 border-primary-200' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={enabled ? '' : 'opacity-50'}>
            {icon}
          </div>
          <div>
            <p className={`font-medium ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>{name}</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-100 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm peer-checked:bg-green-500"></div>
        </label>
      </div>
      
      {enabled && (
        <div className="mt-3 pl-13">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {inputLabel}
          </label>
          {isTextArea ? (
            <textarea
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder={inputPlaceholder}
              className="input"
              rows={3}
            />
          ) : (
            <input
              type="text"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder={inputPlaceholder}
              className="input"
            />
          )}
          <p className="text-xs text-gray-500 mt-1">{helpText}</p>
        </div>
      )}
    </div>
  );
};

export default PaymentsTab;